import React, { useEffect, useState } from "react"
import { FileText, Eye } from "lucide-react"
import { getAllSummaries as fetchAllSummaries, getSummaryByCvId, getCvById } from "../apis/api"
import * as localStorageManager from "../utils/localStorageManager"
import { Summary } from "../utils/localStorageManager"
import { formatTimestamp } from "../utils/utils"

// Wrapper function to use our utility
const formatUploadTime = (timestamp: string): string => {
  return formatTimestamp(timestamp);
};

const SummariesPage: React.FC = () => {
  const [summaries, setSummaries] = useState<Summary[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingCvId, setLoadingCvId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        // First, check if we have cached summaries
        const cachedSummaries = localStorageManager.getAllSummaries();
        if (cachedSummaries && cachedSummaries.length > 0) {
          console.log("Using cached summaries", cachedSummaries);
          setSummaries(cachedSummaries);
          setLoading(false);
          return;
        }
        
        // If no cache or expired, fetch from API
        console.log("Fetching summaries from API");
        const data = await fetchAllSummaries();
        console.log("API returned summaries:", data);
        
        if (data && Array.isArray(data)) {
          setSummaries(data);
          
          // Store the fetched summaries in local storage
          localStorageManager.storeAllSummaries(data);
        } else {
          console.error("Invalid summaries data format:", data);
          setError("Failed to load summaries: Invalid data format");
        }
      } catch (err) {
        console.error("Failed to fetch summaries:", err);
        setError("Failed to load summaries. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    fetchSummaries()
  }, [])

  const handleViewSummary = async (cvId: string, collectionId: string = "scoville") => {
    // Store the current fileId and collectionId before navigating
    localStorageManager.storeFileId(cvId);
    localStorageManager.storeCollectionId(collectionId);
    
    // Set loading flag for this specific CV only
    setLoadingCvId(cvId);
    
    try {
      // Check if we have the summary in our state first
      const selectedSummary = summaries.find(s => s.cv_id === cvId);
      
      // If we have the summary in memory and it has content
      if (selectedSummary && selectedSummary.summary) {
        console.log("Using summary from current state");
        localStorageManager.storeSummary(cvId, selectedSummary.summary);
      } else {
        // Check if we have the summary in local storage
        const cachedSummary = localStorageManager.getCachedSummary(cvId);
        
        if (cachedSummary) {
          console.log("Using cached summary from local storage");
          localStorageManager.storeSummary(cvId, cachedSummary);
        } else {
          // If not in cache, fetch from API
          console.log("Fetching summary from API");
          try {
            const summaryText = await getSummaryByCvId(cvId, collectionId);
            if (summaryText) {
              localStorageManager.storeSummary(cvId, summaryText);
              
              // Update the summary in our state list too for future use
              if (selectedSummary) {
                const updatedSummary = { ...selectedSummary, summary: summaryText };
                localStorageManager.updateSummaryInList(updatedSummary);
              }
            }
          } catch (error) {
            console.error("Failed to fetch summary:", error);
            // Continue navigation even if summary fetch fails
          }
        }
      }
      
      // Check if we have a cached PDF URL
      const cachedPdfUrl = localStorageManager.getCachedPdfUrl(cvId);
      
      if (!cachedPdfUrl) {
        // If no cached URL, fetch from API
        console.log("Fetching CV URL from API");
        try {
          const urlData = await getCvById(cvId, collectionId);
          if (urlData && urlData.url) {
            localStorageManager.storePdfUrl(cvId, urlData);
          }
        } catch (error) {
          console.error("Failed to fetch CV URL:", error);
          // Continue navigation even if URL fetch fails
        }
      } else {
        console.log("Using cached PDF URL");
      }
      
    } catch (error) {
      console.error("Error in handleViewSummary:", error);
    } finally {
      setLoadingCvId(null);
      // Navigate to the root URL first to ensure a clean mount of the dashboard
      // This prevents the multiple refreshes when clicking from summaries page
      // window.location.href = `/${window.location.hash ? '#/' : ''}dashboard?cv_id=${cvId}&collection_id=${collectionId}`;
      window.location.href = `/`;
    }
  }
  
  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          History
        </h2>

        {loading && (
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading summaries...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-4 border border-red-300">
            {error}
          </div>
        )}

        {!loading && summaries.length === 0 && (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No summaries yet</h3>
            <p className="text-gray-600">Upload a resume on the Dashboard to generate a summary</p>
          </div>
        )}

        {!loading && summaries.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Table Header */}
            <div className="grid grid-cols-12 p-4 font-medium text-gray-700 border-b border-gray-200 bg-gray-50">
              <div className="col-span-6">Original Filename</div>
              <div className="col-span-4">Upload Time</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            
            {/* Table Rows */}
            {summaries.map((summary) => (
              <div
                key={summary.cv_id}
                className="grid grid-cols-12 p-4 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors items-center"
              >
                <div className="col-span-6 flex items-center gap-3">
                  <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <div className="truncate">
                    <h3 className="font-medium text-gray-900 truncate" title={summary.fileName}>
                      {summary.fileName}
                    </h3>
                  </div>
                </div>
                
                <div className="col-span-4 text-gray-500">
                  {formatUploadTime(summary.createdAt)}
                </div>
                
                <div className="col-span-2 flex items-center gap-2 justify-end">
                  <button
                    className="p-2 text-gray-500 hover:text-blue-600 transition-colors rounded-lg
                           hover:bg-blue-50"
                    title="View resume and summary"
                    onClick={() => handleViewSummary(summary.cv_id, summary.collection_id)}
                    disabled={loadingCvId === summary.cv_id}
                  >
                    {loadingCvId === summary.cv_id ? (
                      <span className="animate-spin h-4 w-4 border-b-2 border-blue-600 rounded-full inline-block"></span>
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default SummariesPage
